import { createContext, type Context } from 'react';

import type { SessionStateResult } from './useCommentAgentSession.ts';
import type { CommentAgentSessionMap } from './usePageCommentAgentSessions.ts';

export type SharedCommentAgentSessionsSnapshot = {
	sessionMap: CommentAgentSessionMap;
	/** Preserve source associations even after the visible status row completes. */
	conversationMap: ReadonlyMap<string, string>;
	states: ReadonlyMap<string, SessionStateResult>;
};

export const SharedCommentAgentSessionsContext: Context<SharedCommentAgentSessionsSnapshot | null> =
	createContext<SharedCommentAgentSessionsSnapshot | null>(null);

export type SharedCommentAgentSessions = {
	getSnapshot: () => SharedCommentAgentSessionsSnapshot;
	subscribe: (listener: () => void) => () => void;
	publish: (snapshot: SharedCommentAgentSessionsSnapshot) => void;
	retainOwner: () => () => void;
};

const EMPTY_SNAPSHOT: SharedCommentAgentSessionsSnapshot = {
	sessionMap: new Map(),
	conversationMap: new Map(),
	states: new Map(),
};
let stores = new WeakMap<object, Map<string, SharedCommentAgentSessions>>();

/** Shared between the mounted publisher and a comments panel in a separate React branch. */
export function getSharedCommentAgentSessions(
	environment: object,
	cloudId: string,
	userId: string,
	contentId: string,
): SharedCommentAgentSessions {
	let scopedStores = stores.get(environment);
	if (!scopedStores) {
		scopedStores = new Map();
		stores.set(environment, scopedStores);
	}
	const scopeStores = scopedStores;
	const key = JSON.stringify([cloudId, userId, contentId]);
	const existing = scopeStores.get(key);
	if (existing) return existing;

	let snapshot = EMPTY_SNAPSHOT;
	let owners = 0;
	const listeners = new Set<() => void>();
	const releaseUnusedStore = () => {
		// Effect replay and a panel mounting in another branch can retain this scope synchronously.
		queueMicrotask(() => {
			if (owners === 0 && listeners.size === 0 && scopeStores.get(key) === store) {
				scopeStores.delete(key);
			}
		});
	};
	const publish = (next: SharedCommentAgentSessionsSnapshot) => {
		if (snapshot === next) return;
		snapshot = next;
		for (const listener of listeners) listener();
	};
	const store: SharedCommentAgentSessions = {
		getSnapshot: () => snapshot,
		subscribe: (listener) => {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
				releaseUnusedStore();
			};
		},
		publish,
		retainOwner: () => {
			owners += 1;
			let released = false;
			return () => {
				if (released) return;
				released = true;
				owners -= 1;
				if (owners === 0) publish(EMPTY_SNAPSHOT);
				releaseUnusedStore();
			};
		},
	};
	scopeStores.set(key, store);
	return store;
}

export function clearSharedCommentAgentSessionsForTesting(): void {
	stores = new WeakMap();
}
